/**
 * Consumables bar — the little stock counters (apple / strawberry / meat /
 * revive kit) shown on the left of the AÇÕES toolbar inside the PiP.
 *
 * It reflects `state.consumables` and animates a chip when its item is used:
 * the chip pulses and a floating "−1" drifts up. Counts refresh on every
 * `state:dirty` (drops, shop buys, eating) so it always matches the save.
 *
 * @module ui/consumables
 */

import { bus } from '../core/events.js';

/** Emoji fallback shown until/if the pixel icon fails to load. */
const FOOD_EMOJI = { apple: '🍎', strawberry: '🍓', meat: '🍖' };

/**
 * Render the consumables bar into a container and keep it live.
 * Works for any document — pass a container in the native PiP window and it
 * styles/creates nodes in that window too (uses container.ownerDocument).
 *
 * @param {HTMLElement} container the mount point (e.g. #pd-consumables)
 * @param {object} state the game state
 * @param {object} data balance tables (uses data.loot.foods / reviveKit)
 * @returns {{refresh:()=>void, destroy:()=>void}}
 */
export function initConsumablesBar(container, state, data) {
  if (!container) return { refresh() {}, destroy() {} };
  const doc = container.ownerDocument;
  const foods = data.loot?.foods ?? {};
  const foodIds = Object.keys(foods);

  const chip = (cid, iconKey, emoji, label) => `
    <span class="pd-consume-chip" data-cid="${cid}" title="${label}">
      <span class="pd-consume-ic">
        <img src="assets/sprites/items/icon_${iconKey}.png" alt="${label}" />
        <span class="pd-consume-emoji">${emoji}</span>
      </span>
      <span class="pd-consume-n">0</span>
    </span>`;

  container.innerHTML =
    foodIds.map((id) => chip(`food:${id}`, foods[id].icon, FOOD_EMOJI[id] ?? '🍎', foods[id].label)).join('') +
    chip('kit', data.loot?.reviveKit?.icon ?? 'revive_kit', '⛑️', data.loot?.reviveKit?.label ?? 'Kit de Reviver') +
    `<button class="pd-consume-add" title="Comprar consumíveis (Loja)" aria-label="Loja">+</button>`;

  // The "+" opens the consumables shop (gold only). Lives right after the kit so
  // the player buys more of exactly what the bar shows.
  container.querySelector('.pd-consume-add')?.addEventListener('click', () => bus.emit('ui:openShop'));

  // Emoji is the fallback: hide it once the real pixel icon loads; hide the
  // broken <img> if it 404s so only the emoji shows.
  container.querySelectorAll('.pd-consume-ic').forEach((wrap) => {
    const img = wrap.querySelector('img');
    const emoji = wrap.querySelector('.pd-consume-emoji');
    if (img.complete && img.naturalWidth > 0) emoji.style.display = 'none';
    img.addEventListener('load', () => { if (img.naturalWidth > 0) emoji.style.display = 'none'; });
    img.addEventListener('error', () => { img.style.display = 'none'; });
  });

  const setChip = (cid, n) => {
    const el = container.querySelector(`[data-cid="${cid}"]`);
    if (!el) return;
    el.querySelector('.pd-consume-n').textContent = n;
    el.classList.toggle('pd-consume-zero', n <= 0);
  };

  const refresh = () => {
    foodIds.forEach((id) => setChip(`food:${id}`, state.consumables?.foods?.[id] ?? 0));
    setChip('kit', state.consumables?.reviveKits ?? 0);
  };

  /** Pulse a chip + float a delta when its consumable is used/gained. */
  const pulse = (cid, delta = -1) => {
    const el = container.querySelector(`[data-cid="${cid}"]`);
    if (el) {
      el.classList.remove('pd-consume-pulse');
      void el.offsetWidth; // restart the animation
      el.classList.add('pd-consume-pulse');
      const f = doc.createElement('span');
      f.className = 'pd-consume-float';
      f.textContent = (delta > 0 ? '+' : '') + delta;
      el.appendChild(f);
      setTimeout(() => f.remove(), 800);
    }
    refresh();
  };

  const offs = [
    bus.on('state:dirty', refresh),
    bus.on('combat:food_eat', (e) => pulse(`food:${e.foodId}`, -1)),
    bus.on('combat:hero_revived_kit', () => pulse('kit', -1)),
  ];
  refresh();

  return {
    refresh,
    destroy() {
      offs.forEach((off) => off && off());
      container.innerHTML = '';
    },
  };
}
